import {Alert, Button, Card, Col, Modal, Row, Space, Spin, Typography} from 'antd';
import {DeleteOutlined, DownOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SaveOutlined} from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { HttpError, useDataProvider, useGetIdentity, useNotify, useQuery, useRedirect } from 'react-admin';
import { ExtendedDataProvider, ServiceItem } from '@src/types';
import SchemaBasedForm from './FormGenerator/SchemaBasedForm';
import { useBuilderWizardContext } from '../BuilderWizardProvider';
import AddApplicationModal from './AddApplicationModal';
import ApplicationPanel from './ApplicationPanel';
import useDeclarationDataEditor from './useDeclarationDataEditor';
import ValidationErrors from '../ValidationErrors';
import { isSubmitButtonVisible, rekeyDeclaration } from './utils'
import TeamMetadataModal from './TeamMetadataModal';
import {getActiveMembership} from "@src/components/Common/utils";
import ChangeInstancesValidators from "../SubComponents/ChangeInstancesValidators";
import '../SubmissionBuilder.css';

const { confirm } = Modal;

interface ServiceItemContainer {
    schema: any;
    serviceItem: ServiceItem | null;
    service: string | null;
}

const SubmissionEditor = () => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const [newApplicationsCreated, setNewApplicationsCreated] = useState<string[]>([])
    const [isAppModalOpen, setIsAppModalOpen] = useState<boolean>(false)
    const [isTeamMetadataModalOpen, setIsTeamMetadataModalOpen] = useState<boolean>(false)
    const { identity } = useGetIdentity()
    const [isSchemaLoading, setIsSchemaLoading] = useState<boolean>(false)
    /** why the pane is empty, when it is empty for a reason */
    const [schemaError, setSchemaError] = useState<string | null>(null)

    const [dryRun, setDryRun] = useState<boolean> (false)
    const [dryRunChangeInstances, setDryRunChangeInstances] = useState<Record<string, any>[] | null> (null)

    const [serviceItemsWithErrors, setServiceItemsWithErrors] = useState<ServiceItem[]>([]);
    const notify = useNotify();
    const redirect = useRedirect();
    const [selectedServiceItemContainer, setSelectedServiceItemContainer] = useState<ServiceItemContainer>({
        schema: null,
        serviceItem: null,
        service: null
    });
    const [isLoading, setIsLoading] = useState(true);

    const activeMembership = getActiveMembership()
    const teamName =  activeMembership?.team?.name

    const { 
        setSelectedApplication, 
        selectedApplication, 
        setSelectedService, 
        selectedService, 
        setIsCurrentStepValid, 
        setDeclarationData, 
        declarationData, 
        setValidationErrors, 
        validationErrors,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        saveLastSubmittedState,
        setInitialLastSubmittedState,
        checkForUnsavedChanges,
    } = useBuilderWizardContext();
    const {
        getServiceItemData,
        partialUpdateDeclaration,
        deleteAppFromDeclaration,
        partialDeleteDeclaration,
        partialUpdateAppMetadata,
        partialUpdateTeamMetadata,
        renameAppInDeclaration
    } = useDeclarationDataEditor({ declarationData, setDeclarationData });

    const { Title } = Typography;
    const { data: submissionListData, loaded, error: submissionListError } = useQuery({
        type: 'getList',
        resource: 'submissions',
        payload: {
            pagination: { page: 1, perPage: 1 },
            sort: { field: "id", order: "DESC" },
            filter: {}
        }
    });

    const validateSubmission = (dryRun: boolean) => {
        setDryRun(dryRun)
        dataProvider.createAction ('submissions', {
            data: declarationData,
            verb: `validate/?${dryRun ? "dry_run=true" : ""}`
        })
            .then((response: any) => {
                if (response?.data?.is_valid) {
                    notify('Validation successful', "success")
                    setValidationErrors(false)
                    if (dryRun)
                        setDryRunChangeInstances (response.data.change_instances)
                }
            }).catch((error: HttpError) => {
                console.error(error.body)
                notify('Validation failed', "error")
                setValidationErrors(error.body.errors)
            })
    }

    const submitSubmission = () => {
        dataProvider.createAction('submissions', { data: declarationData, verb: 'submit/' })
            .then((response: any) => {
                if(response?.data?.message === "no change detected") {
                    notify('No changes detected', "info");
                } else if(response?.data?.id) {
                    saveLastSubmittedState();
                    notify('Submission successful', "success");
                    redirect(`/submissions/${response?.data?.id}`);
                }
            }).catch((error: HttpError) => {
                console.error(error.body);
                notify('Submission failed', "error");
            });
    }

    useEffect(() => {
        // Guard: Wait for teamName
        if (!teamName) {
            return;
        }

        // --- Primary Check: Do we already have data from the context (STORAGE_KEY)? ---
        if (declarationData && Object.keys(declarationData).length > 0) {
            // Data exists. Ensure loading is false and do nothing else.
            // This prevents overwriting work-in-progress.
            // The one thing that is changed is the key it is under: work saved
            // before a rename is still this team's work, and has to be
            // submitted under the name the team goes by now.
            const rekeyed = rekeyDeclaration(declarationData, teamName);
            if (rekeyed !== declarationData) {
                setDeclarationData(rekeyed);
                notify(`This team has been renamed — the declaration is now under ${teamName}`, 'info');
            }
            if (isLoading) setIsLoading(false);
            return;
        }

        // --- If we reach here, declarationData is not set yet. Proceed with initial load. ---

        // Guard: Wait for the API query to finish (either load or error)
        // Only set isLoading true if we are actually going to fetch/process API results.
        if (!loaded && !submissionListError) {
             setIsLoading(true); // We are actively waiting for the initial load API call
            return;
        }

        // --- API is done, and we need to set initial state ---
        // We should still be in the isLoading=true state from the check above.
        
        let initialData = null; // Variable to hold the data we determine

        if (loaded) {
            const latestSubmission = submissionListData?.[0];
            if (latestSubmission?.request_json) {
                // Case 1: API success, valid data found — under the name the
                // team had when it was submitted, which is not necessarily the
                // name it has now
                initialData = rekeyDeclaration(latestSubmission.request_json, teamName);
            } else {
                // Case 2: API success, but no data or missing request_json
                if (latestSubmission && !latestSubmission.request_json) {
                    console.error('API returned submission with no request_json');
                }
                initialData = { [teamName]: {} }; // Default empty object
            }
        } else { // Implies submissionListError is true
            // Case 3: API error
            console.error('API error loading submissions, creating empty state', submissionListError);
            initialData = { [teamName]: {} }; // Default empty object
        }

        // Now, set the state based on the determined initialData
        // This is the *first* time we are setting data in this editing session.
        setInitialLastSubmittedState(initialData);
        setDeclarationData(initialData);
        setIsLoading(false); // Finished setting initial state

    }, [
        // Dependencies:
        teamName,             // Needed for guards and default object
        declarationData,      // Primary check to see if data exists
        loaded,               // API state
        submissionListError,  // API state
        submissionListData,   // API data
        isLoading,            // Read isLoading state
        setIsLoading,         // Need to set loading state
        setDeclarationData,   // Need to set data state
        setInitialLastSubmittedState // Need to set initial submitted state
    ]);

    useEffect(() => {
        if (declarationData) {
            const hasChanges = checkForUnsavedChanges();
            setHasUnsavedChanges(hasChanges);
            
            // Reset validation when there are unsaved changes
            if (hasChanges) {
                setValidationErrors(null);
                // the proposed changes were computed from the declaration as it
                // was; leaving them up would describe a submission that is no
                // longer the one the Submit button would make
                setDryRunChangeInstances(null);
            }
        }
    }, [declarationData, checkForUnsavedChanges, setHasUnsavedChanges]);

    useEffect(() => {
        setIsCurrentStepValid(serviceItemsWithErrors.length == 0)
    }, [serviceItemsWithErrors])

    const getServiceSchema = async (serviceName: string | Number | null) => {
        return dataProvider.getList('browse_services', {
            pagination: { page: 1, perPage: 20 },
            sort: { field: "name", order: "asc" },
            filter: {
                'name_exact': serviceName
            }
        }
        ).then((data: any) => {
            return data
        })
    }
    const handleItemUpdate = (formData: any) => {
        if(selectedServiceItemContainer.serviceItem && formData?.name) 
        {
        const updatedServiceItem = partialUpdateDeclaration({
            applicationName: selectedApplication,
            serviceName: selectedServiceItemContainer.serviceItem?.serviceName,
            serviceItemName: selectedServiceItemContainer.serviceItem?.name,
            updatedData: formData
        })
        setSelectedServiceItemContainer(prev => ({
            ...prev,
            serviceItem: updatedServiceItem
        }));
    }
    }

    const handleSuccessSubmit = (formData: any) => {
        setServiceItemsWithErrors(prevItemsWithErrors =>
            prevItemsWithErrors.filter(erroredItem => !(erroredItem.applicationName === selectedServiceItemContainer.serviceItem!.applicationName && erroredItem.name === selectedServiceItemContainer.serviceItem!.name))
        )
    }

    const handleDeleteApp = (applicationName: string) => {
        deleteAppFromDeclaration({ applicationName })
    }

    const handleRenameApp = (previousName: string, newName: string) => {
        renameAppInDeclaration({ applicationName: previousName, updatedApplicationName: newName })
    }

    const handleDeleteItem = () => {
        confirm({
            okText: "Remove",
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure you want to remove this service item from the declaration?',
            onOk() {
                partialDeleteDeclaration({
                    applicationName: selectedApplication,
                    serviceName: selectedServiceItemContainer.serviceItem?.serviceName!,
                    serviceItemName: selectedServiceItemContainer.serviceItem?.name!
                })
                setSchemaError(null)
                setSelectedServiceItemContainer({
                    schema: null,
                    serviceItem: null,
                    service: null
                })
            },
        });
    }
    const handleItemError = (errors: any) => {
        setServiceItemsWithErrors(prevItemsWithErrors => [...prevItemsWithErrors, selectedServiceItemContainer.serviceItem!]);
        errors && setIsCurrentStepValid(false)
    }

    const onAddNewApp = (appName: string) => {
        if (!teamName) {
            return
        }
        const amendedData = JSON.parse(JSON.stringify(declarationData));
        if (amendedData[teamName][appName]) {
            console.warn("app already exists")
            notify('Application with that name already exists', 'error')
            return;
        }
        setNewApplicationsCreated((prevData) => [...prevData, appName]);
        amendedData[teamName][appName] = { services: {} }
        setDeclarationData(amendedData)
        setSelectedApplication(appName)
    }

    const onEditTeamMetadata = (teamMetadata: object | null) => {
        partialUpdateTeamMetadata({ updatedData: teamMetadata })
    }

    const onAddServiceItem = async (serviceName: string, serviceItemName: string) => {
        setSchemaError(null);
        const schema = await getServiceSchema(serviceName);
        const serviceSchema = schema.data[0]?.schema;

        const updatedServiceItem = partialUpdateDeclaration({
            applicationName: selectedApplication,
            serviceName: serviceName,
            serviceItemName: serviceItemName,
            updatedData: { name: serviceItemName },
        })

        // the service was picked from the catalogue a moment ago, so this is
        // the unlikely case — but it is the same blank pane if it is not said
        if (!serviceSchema?.schema) {
            setSchemaError(`No schema found for the service "${serviceName}".`);
        }

        setSelectedServiceItemContainer({
            schema: serviceSchema?.schema ? serviceSchema : null,
            serviceItem: updatedServiceItem,
            service: serviceName
        });
        setSelectedService(serviceName);
    }

    const onEditAppMetadata = (appName: string, appMetadata: object) => {
        partialUpdateAppMetadata({ applicationName: appName, updatedData: appMetadata },)
    }


    /**
     * The nav hands over the application, the service and the item by name.
     * It used to hand over an antd tree node whose key packed the last two
     * into one string around a unit separator, for this to split apart again.
     *
     * Every way this can fail used to end in the same blank pane, showing the
     * "select an item" text over an item that was already selected. A
     * declaration keeps the service's name as it was written, so an item whose
     * service has since been renamed — or that this team can no longer see —
     * finds no schema, and that is worth saying rather than swallowing.
     */
    const handleSelectServiceItem = async (applicationName: string, serviceName: string, serviceItemName: string) => {
        setIsSchemaLoading(true);
        setSchemaError(null);
        setSelectedApplication(applicationName);
        setSelectedService(serviceName);
        try {
            const serviceItem: ServiceItem = getServiceItemData(applicationName, serviceName, serviceItemName, declarationData);
            const schema = await getServiceSchema(serviceItem.serviceName);
            const serviceSchema = schema.data[0]?.schema;

            if (!serviceSchema?.schema) {
                setSelectedServiceItemContainer({ schema: null, serviceItem, service: serviceItem.serviceName });
                setSchemaError(
                    `No schema found for the service "${serviceItem.serviceName}". It may have been renamed or removed, or your team may no longer have access to it.`
                );
                return;
            }

            setSelectedServiceItemContainer({
                schema: serviceSchema,
                serviceItem: serviceItem,
                service: serviceItem.serviceName
            });
        } catch (error) {
            console.error('Could not open service item', error);
            setSelectedServiceItemContainer({ schema: null, serviceItem: null, service: null });
            setSchemaError(`Could not open "${serviceItemName}". ${(error as Error)?.message ?? ''}`.trim());
        } finally {
            setIsSchemaLoading(false);
        }
    }


    /**
     * One application is open at a time, and the one that is open is the one
     * whose items the form belongs to — so opening another closes this one and
     * clears the form with it.
     *
     * Clicking the open one used to re-select it, which left the disclosure
     * saying it was open and doing nothing about it.
     */
    const handleAppSelect = (application: string) => {
        setSchemaError(null)
        setSelectedServiceItemContainer({
            schema: null,
            serviceItem: null,
            service: null
        })
        setSelectedApplication(previous => (previous === application ? null : application))
    }

    if (identity && !teamName) {
        return <Alert message="Please create or join a team to use the Submission Builder" type="error" />
    }

    if (isLoading) {
        return <Spin style={{ 'width': '100%' }} />
    }

    if (!declarationData || !teamName || !declarationData[teamName] || typeof declarationData[teamName] !== 'object') {
        const initialData = { [teamName]: {} };
        setDeclarationData(initialData);
        return <Spin style={{ 'width': '100%' }} />
    }

    const isRemoteValid = (typeof validationErrors === 'boolean' && validationErrors === false);
    const hasSchema = Boolean(selectedServiceItemContainer.schema);
    // `metadata` sits alongside the applications under the team, and is not one
    const applicationNames = Object.keys(declarationData[teamName]).filter(name => name !== 'metadata');
    return (
        <Card
            className="header-solid netorca-sb"
            bordered={false}
        >
            {validationErrors && <ValidationErrors errors={validationErrors} />}
            {/* One row of two panes. They carry no fixed spans: the sidebar's
                nested names need a wider share of a narrow viewport than of a
                wide one, and below `md` both go full width and the row becomes
                two. */}
            <Row>
                <Col className="sb-sidebar" xs={24} md={9} lg={7} xl={6}>
                    <nav className="sb-apps" aria-label="Applications and service items">
                        {applicationNames.length === 0 && (
                            <p className="sb-apps__empty">No applications yet.</p>
                        )}
                        {applicationNames.map((application: string) => (
                            <ApplicationPanel
                                key={application}
                                serviceItemsWithErrors={serviceItemsWithErrors}
                                application={application}
                                selectedServiceItem={selectedServiceItemContainer.serviceItem}
                                setSelectedService={setSelectedService}
                                selectedService={selectedService}
                                appDeclarationData={declarationData[teamName]?.[application]}
                                onSelectServiceItem={(serviceName, serviceItemName) =>
                                    handleSelectServiceItem(application, serviceName, serviceItemName)
                                }
                                onAddServiceItem={onAddServiceItem}
                                onEditAppMetadata={onEditAppMetadata}
                                onRemoveApp={handleDeleteApp}
                                onRenameApp={handleRenameApp}
                                newApplicationsCreated={newApplicationsCreated}
                                isActive={selectedApplication === application}
                                onToggle={handleAppSelect}
                            />
                        ))}
                    </nav>
                    <div className="sb-sidebar__actions">
                        <Button block icon={<PlusOutlined />} onClick={() => setIsAppModalOpen(true)}>New Application</Button>
                        <Button block icon={<EditOutlined />} onClick={() => setIsTeamMetadataModalOpen(true)}>Team Metadata</Button>
                    </div>
                    {isTeamMetadataModalOpen && <TeamMetadataModal metadata={declarationData[teamName]?.metadata} handleModalSubmit={onEditTeamMetadata} modalVisible={isTeamMetadataModalOpen} setModalVisible={setIsTeamMetadataModalOpen} />}
                    {isAppModalOpen && <AddApplicationModal handleModalSubmit={onAddNewApp} existingNames={applicationNames} modalVisible={isAppModalOpen} setModalVisible={setIsAppModalOpen} />}
                </Col>

                {/* The three states of this pane — loading, nothing selected,
                    a form — are mutually exclusive, so they are one column
                    rather than two overlapping ones. As two, the empty state
                    and the form pane both rendered at 18 alongside the
                    sidebar's 6, overflowing the 24-column grid and dropping
                    an empty full-width column below the fold. */}
                <Col className="sb-main" xs={24} md={15} lg={17} xl={18}>
                    {isSchemaLoading && (
                        <div className="sb-main__center">
                            <Spin />
                        </div>
                    )}

                    {!isSchemaLoading && !hasSchema && schemaError && (
                        <div className="sb-main__center">
                            <Alert
                                className="sb-main__error"
                                type="error"
                                showIcon
                                message="This service item cannot be edited"
                                description={schemaError}
                            />
                        </div>
                    )}

                    {!isSchemaLoading && !hasSchema && !schemaError && (
                        <div className="sb-main__center">
                            <Title className="sb-empty" level={5} role="status">
                                {applicationNames.length === 0
                                    ? "Click the 'New Application' button to get started"
                                    : "Select or Add a Service Item"}
                            </Title>
                        </div>
                    )}

                    {!isSchemaLoading && hasSchema && (
                        <div className="sb-form">
                            <SchemaBasedForm
                                onError={handleItemError}
                                onSave={handleItemUpdate}
                                onSubmit={handleSuccessSubmit}
                                formData={selectedServiceItemContainer.serviceItem?.data}
                                key={selectedServiceItemContainer.schema?.id}
                                style={{ maxWidth: '600px', width: '100%' }}
                                recordId={selectedServiceItemContainer.schema?.id}
                                schema={selectedServiceItemContainer.schema?.schema}
                            />
                            <div className="sb-form__actions">
                                <Button icon={<DeleteOutlined />} onClick={handleDeleteItem} danger>
                                    Remove Service item
                                </Button>
                            </div>
                        </div>
                    )}
                </Col>
            </Row>

            <Row className="sb-footer" justify="center">
                <Col>
                    <Space wrap align="center">
                        <Button onClick={() => validateSubmission(true)}>Validate</Button>
                        {
                            isSubmitButtonVisible() &&
                                <Button
                                    type='primary'
                                    disabled={!isRemoteValid}
                                    onClick={() => submitSubmission()}
                                >
                                   Submit
                                </Button>
                        }
                        {isSubmitButtonVisible() && hasUnsavedChanges && (
                            <span className="sb-unsaved">
                                <SaveOutlined aria-hidden="true" />
                                Unsubmitted changes
                            </span>
                        )}
                    </Space>
                </Col>
            </Row>

            {dryRun && dryRunChangeInstances !== null && (
                <Row className="sb-dryrun">
                    <Col span={24}>
                        <ChangeInstancesValidators change_instances={dryRunChangeInstances} />
                    </Col>
                </Row>
            )}
        </Card>
    )
}

export default SubmissionEditor;